import { useCallback, useEffect, useRef, useState } from 'react'
import {
  estadoInicialDaEtapa,
  perfilAnimacaoDaEtapa,
  tecnicaCortePorId,
  type EstadoInicialFerramenta,
  type EtapaDemonstracaoCorte,
  type IdTecnicaCorte,
  type TecnicaCorte,
} from '../../dominio/didatico/tecnicasCorteCatalogo'

export type EstadoAnimacaoPlayer =
  | 'pausado'
  | 'enquadrando'
  | 'aproximando'
  | 'contato'
  | 'executando'
  | 'recuando'
  | 'observando'
  | 'concluido'
  | 'erro'

export type VelocidadePlayer = 0.5 | 1 | 2
type FaseAtiva = Exclude<EstadoAnimacaoPlayer, 'pausado' | 'concluido' | 'erro'>

export interface UsePlayerOficinaCorteProps {
  tecnicaInicialId?: IdTecnicaCorte
  /** Retorna falso quando o motor didático recusa a operação. */
  aoConcluirEtapa: (etapa: EtapaDemonstracaoCorte) => boolean | void
  aoMudarTecnica?: (novaTecnica: TecnicaCorte) => void
  aoReiniciar?: () => void
}

export interface PlayerOficinaCorte {
  tecnica: TecnicaCorte
  indiceEtapa: number
  etapaAtual: EtapaDemonstracaoCorte
  estadoAnimacao: EstadoAnimacaoPlayer
  /** Fase que permanece desenhada enquanto a demonstração está pausada. */
  faseVisual: EstadoAnimacaoPlayer
  reproduzindo: boolean
  progressoFase: number
  progressoFerramenta: number
  afastamentoFerramenta: number
  /**
   * 0 com as garras no estado inicial do passo (no ar, ou soltando a peça),
   * 1 na pose de contato e execução.
   */
  preparoFerramenta: number
  /** Como as garras chegam à peça neste passo; `null` sem ferramenta de garra. */
  estadoInicialFerramenta: EstadoInicialFerramenta | null
  ferramentaVisivel: boolean
  zonaEmDestaque: boolean
  velocidade: VelocidadePlayer
  etapasConcluidas: readonly string[]
  erro: string | null
  cameraGuiada: boolean
  versaoFocoCamera: number
  selecionarTecnica: (id: IdTecnicaCorte) => void
  reproduzir: () => void
  pausar: () => void
  alternarReproducao: () => void
  reiniciar: () => void
  definirVelocidade: (v: VelocidadePlayer) => void
  alternarCameraGuiada: () => void
  focarZonaAtual: () => void
  tornarCameraManual: () => void
}

const AFASTAMENTO_ESPERA_METROS = 0.38
/** Recuo só para soltar a peça quando o passo seguinte usa a mesma ferramenta. */
const AFASTAMENTO_SOLTURA_METROS = 0.06
/** Fração da aproximação em que as garras chegam à pose de contato: abrem no ar. */
const FIM_PREPARO_NA_APROXIMACAO = 0.75
const suavizar = (p: number) => 0.5 - Math.cos(p * Math.PI) / 2

/** Dois passos seguidos com a mesma ferramenta: ela fica em cena entre eles. */
function mesmaFerramenta(a: EtapaDemonstracaoCorte | undefined, b: EtapaDemonstracaoCorte | undefined): boolean {
  return Boolean(a && b && a.ferramenta !== 'nenhuma' && a.ferramenta === b.ferramenta)
}

function proximaFase(fase: FaseAtiva, etapa: EtapaDemonstracaoCorte): FaseAtiva | 'concluido' {
  if (fase === 'enquadrando') return etapa.ferramenta === 'nenhuma' ? 'executando' : 'aproximando'
  if (fase === 'aproximando') return 'contato'
  if (fase === 'contato') return 'executando'
  if (fase === 'executando') return etapa.ferramenta === 'nenhuma' ? 'observando' : 'recuando'
  if (fase === 'recuando') return 'observando'
  return 'concluido'
}

function duracaoDaFase(etapa: EtapaDemonstracaoCorte, fase: FaseAtiva): number {
  const perfil = perfilAnimacaoDaEtapa(etapa)
  switch (fase) {
    case 'enquadrando': return perfil.enquadrandoMs
    case 'aproximando': return perfil.aproximandoMs
    case 'contato': return perfil.contatoMs
    case 'executando': return perfil.executandoMs
    case 'recuando': return perfil.recuandoMs
    case 'observando': return perfil.observandoMs
  }
}

/** Relógio reproduzível da demonstração; a cena só recebe poses e nunca decide operações. */
export function usePlayerOficinaCorte({
  tecnicaInicialId = 'portaDobradicas',
  aoConcluirEtapa,
  aoMudarTecnica,
  aoReiniciar,
}: UsePlayerOficinaCorteProps): PlayerOficinaCorte {
  const [tecnicaId, setTecnicaId] = useState<IdTecnicaCorte>(tecnicaInicialId)
  const tecnica = tecnicaCortePorId(tecnicaId)
  const [indiceEtapa, setIndiceEtapa] = useState(0)
  const [estadoAnimacao, setEstadoAnimacao] = useState<EstadoAnimacaoPlayer>('pausado')
  const [velocidade, setVelocidade] = useState<VelocidadePlayer>(1)
  const [progressoFase, setProgressoFase] = useState(0)
  const [progressoFerramenta, setProgressoFerramenta] = useState(0)
  const [afastamentoFerramenta, setAfastamentoFerramenta] = useState(AFASTAMENTO_ESPERA_METROS)
  const [preparoFerramenta, setPreparoFerramenta] = useState(0)
  const [etapasConcluidas, setEtapasConcluidas] = useState<readonly string[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [cameraGuiada, setCameraGuiada] = useState(true)
  const [versaoFocoCamera, setVersaoFocoCamera] = useState(0)

  const etapaAtual = tecnica.etapas[indiceEtapa] ?? tecnica.etapas[0]
  const continuaDaAnterior = mesmaFerramenta(tecnica.etapas[indiceEtapa - 1], etapaAtual)
  const seguePraProxima = mesmaFerramenta(etapaAtual, tecnica.etapas[indiceEtapa + 1])
  const inicioFaseRef = useRef(0)
  const estadoRef = useRef(estadoAnimacao)
  const fasePausadaRef = useRef<FaseAtiva>('enquadrando')
  const etapaRef = useRef(etapaAtual)
  const progressoRef = useRef(0)
  const velocidadeRef = useRef(velocidade)
  const aoConcluirEtapaRef = useRef(aoConcluirEtapa)
  estadoRef.current = estadoAnimacao
  etapaRef.current = etapaAtual
  progressoRef.current = progressoFase
  velocidadeRef.current = velocidade
  aoConcluirEtapaRef.current = aoConcluirEtapa

  const iniciarFase = useCallback((fase: FaseAtiva) => {
    inicioFaseRef.current = performance.now()
    setProgressoFase(0)
    setEstadoAnimacao(fase)
    if (fase === 'enquadrando') setVersaoFocoCamera((versao) => versao + 1)
  }, [])

  const iniciarTecnica = useCallback(() => {
    setIndiceEtapa(0)
    setEtapasConcluidas([])
    setErro(null)
    setProgressoFerramenta(0)
    setAfastamentoFerramenta(AFASTAMENTO_ESPERA_METROS)
    setPreparoFerramenta(0)
    iniciarFase('enquadrando')
  }, [iniciarFase])

  const pausar = useCallback(() => {
    const atual = estadoRef.current
    if (atual !== 'pausado' && atual !== 'concluido' && atual !== 'erro') {
      fasePausadaRef.current = atual
      setEstadoAnimacao('pausado')
    }
  }, [])

  const reproduzir = useCallback(() => {
    if (estadoRef.current === 'concluido') {
      aoReiniciar?.()
      iniciarTecnica()
      return
    }
    if (estadoRef.current === 'erro') return
    if (estadoRef.current === 'pausado') {
      const fase = fasePausadaRef.current
      const duracao = duracaoDaFase(etapaRef.current, fase) / velocidadeRef.current
      inicioFaseRef.current = performance.now() - progressoRef.current * duracao
      setEstadoAnimacao(fase)
    }
  }, [aoReiniciar, iniciarTecnica])

  const alternarReproducao = useCallback(() => {
    if (estadoRef.current === 'erro') {
      aoReiniciar?.()
      iniciarTecnica()
    }
    else if (estadoRef.current === 'pausado' || estadoRef.current === 'concluido') reproduzir()
    else pausar()
  }, [aoReiniciar, iniciarTecnica, pausar, reproduzir])

  const reiniciar = useCallback(() => {
    aoReiniciar?.()
    iniciarTecnica()
  }, [aoReiniciar, iniciarTecnica])

  const selecionarTecnica = useCallback((novaId: IdTecnicaCorte) => {
    aoMudarTecnica?.(tecnicaCortePorId(novaId))
    setTecnicaId(novaId)
    setIndiceEtapa(0)
    setEtapasConcluidas([])
    setErro(null)
    setProgressoFerramenta(0)
    setAfastamentoFerramenta(AFASTAMENTO_ESPERA_METROS)
    setPreparoFerramenta(0)
    inicioFaseRef.current = performance.now()
    setProgressoFase(0)
    setVersaoFocoCamera((versao) => versao + 1)
    setEstadoAnimacao('enquadrando')
  }, [aoMudarTecnica])

  const definirVelocidade = useCallback((novaVelocidade: VelocidadePlayer) => {
    const atual = estadoRef.current
    if (atual !== 'pausado' && atual !== 'concluido' && atual !== 'erro') {
      const duracaoNova = duracaoDaFase(etapaRef.current, atual) / novaVelocidade
      inicioFaseRef.current = performance.now() - progressoRef.current * duracaoNova
    }
    setVelocidade(novaVelocidade)
  }, [])

  useEffect(() => {
    const pausarAoOcultar = () => {
      if (document.visibilityState === 'hidden') pausar()
    }
    document.addEventListener('visibilitychange', pausarAoOcultar)
    return () => document.removeEventListener('visibilitychange', pausarAoOcultar)
  }, [pausar])

  useEffect(() => {
    if (estadoAnimacao === 'pausado' || estadoAnimacao === 'concluido' || estadoAnimacao === 'erro') return
    let animacaoId: number
    const passo = (agora: number) => {
      const fase = estadoAnimacao as FaseAtiva
      const duracao = duracaoDaFase(etapaAtual, fase) / velocidade
      const p = duracao === 0 ? 1 : Math.min(1, (agora - inicioFaseRef.current) / duracao)
      const curva = suavizar(p)
      setProgressoFase(p)

      if (fase === 'aproximando') {
        const partida = continuaDaAnterior ? AFASTAMENTO_SOLTURA_METROS : AFASTAMENTO_ESPERA_METROS
        setAfastamentoFerramenta((1 - curva) * partida)
        setProgressoFerramenta(0)
        setPreparoFerramenta(suavizar(Math.min(1, p / FIM_PREPARO_NA_APROXIMACAO)))
      } else if (fase === 'contato') {
        setAfastamentoFerramenta((1 - curva) * 0.035)
        setProgressoFerramenta(0)
        setPreparoFerramenta(1)
      } else if (fase === 'executando') {
        setAfastamentoFerramenta(0)
        setProgressoFerramenta(curva)
        // Passo da equipe não tem recuo: as garras ficam no estado inicial para o próximo.
        setPreparoFerramenta(etapaAtual.ferramenta === 'nenhuma' ? 0 : 1)
      } else if (fase === 'recuando') {
        // Solta a peça: as garras voltam ao estado inicial (o cortador reabre,
        // o alargador fecha). Se o próximo passo usa a mesma ferramenta, ela só
        // se afasta o bastante para soltar e segue dali para a próxima zona.
        setAfastamentoFerramenta(curva * (seguePraProxima ? AFASTAMENTO_SOLTURA_METROS : AFASTAMENTO_ESPERA_METROS))
        setProgressoFerramenta(1 - curva)
        setPreparoFerramenta(1 - curva)
      }

      if (p >= 1) {
        if (fase === 'executando') {
          const concluida = aoConcluirEtapaRef.current(etapaAtual)
          if (concluida === false) {
            setErro(`Não foi possível concluir “${etapaAtual.titulo}”.`)
            setEstadoAnimacao('erro')
            return
          }
          setEtapasConcluidas((anteriores) => anteriores.includes(etapaAtual.id) ? anteriores : [...anteriores, etapaAtual.id])
        }
        const proxima = proximaFase(fase, etapaAtual)
        inicioFaseRef.current = agora
        if (proxima === 'concluido') {
          if (indiceEtapa < tecnica.etapas.length - 1) {
            setIndiceEtapa((indice) => indice + 1)
            setProgressoFase(0)
            setEstadoAnimacao('enquadrando')
            setVersaoFocoCamera((versao) => versao + 1)
          } else {
            setProgressoFerramenta(0)
            setAfastamentoFerramenta(AFASTAMENTO_ESPERA_METROS)
            setPreparoFerramenta(0)
            setEstadoAnimacao('concluido')
          }
        } else {
          setEstadoAnimacao(proxima)
        }
      }
      animacaoId = requestAnimationFrame(passo)
    }
    animacaoId = requestAnimationFrame(passo)
    return () => cancelAnimationFrame(animacaoId)
  }, [estadoAnimacao, etapaAtual, indiceEtapa, tecnica.etapas.length, velocidade, continuaDaAnterior, seguePraProxima])

  const faseVisual = estadoAnimacao === 'pausado' ? fasePausadaRef.current : estadoAnimacao
  const ferramentaVisivel = etapaAtual.ferramenta !== 'nenhuma'
    && (faseVisual === 'aproximando' || faseVisual === 'contato' || faseVisual === 'executando' || faseVisual === 'recuando'
      || (faseVisual === 'observando' && seguePraProxima)
      || (faseVisual === 'enquadrando' && continuaDaAnterior))
  const zonaEmDestaque = etapaAtual.zonaAlvo !== null && faseVisual !== 'concluido' && faseVisual !== 'erro'

  return {
    tecnica, indiceEtapa, etapaAtual, estadoAnimacao, faseVisual,
    reproduzindo: !['pausado', 'concluido', 'erro'].includes(estadoAnimacao),
    progressoFase, progressoFerramenta, afastamentoFerramenta, preparoFerramenta,
    estadoInicialFerramenta: estadoInicialDaEtapa(etapaAtual),
    ferramentaVisivel, zonaEmDestaque,
    velocidade, etapasConcluidas, erro, cameraGuiada, versaoFocoCamera,
    selecionarTecnica, reproduzir, pausar, alternarReproducao, reiniciar, definirVelocidade,
    alternarCameraGuiada: () => setCameraGuiada((ativa) => !ativa),
    focarZonaAtual: () => setVersaoFocoCamera((versao) => versao + 1),
    tornarCameraManual: () => setCameraGuiada(false),
  }
}
